<template>
  <div class="panel-card">
    <div class="panel-card__header">
      <h3 class="panel-card__title">
        {{ $t('OPPORTUNITY_DASHBOARD.OWNERS.TITLE') }}
      </h3>
      <div v-if="owners.length" class="toggle-group">
        <button
          class="toggle-group__option"
          :class="{ 'toggle-group__option--active': mode === 'count' }"
          @click="mode = 'count'"
        >
          {{ $t('OPPORTUNITY_DASHBOARD.OWNERS.QUANTITY') }}
        </button>
        <button
          class="toggle-group__option"
          :class="{ 'toggle-group__option--active': mode === 'value' }"
          @click="mode = 'value'"
        >
          {{ $t('OPPORTUNITY_DASHBOARD.OWNERS.VALUE') }}
        </button>
      </div>
    </div>

    <table v-if="owners.length" class="owners-table">
      <thead>
        <tr>
          <th />
          <th>{{ $t('OPPORTUNITY_DASHBOARD.OWNERS.COLUMNS.CREATED') }}</th>
          <th>{{ $t('OPPORTUNITY_DASHBOARD.OWNERS.COLUMNS.OPEN') }}</th>
          <th>{{ $t('OPPORTUNITY_DASHBOARD.OWNERS.COLUMNS.WON') }}</th>
          <th>{{ $t('OPPORTUNITY_DASHBOARD.OWNERS.COLUMNS.LOST') }}</th>
          <th>
            {{ $t('OPPORTUNITY_DASHBOARD.OWNERS.COLUMNS.CONVERSION_RATE') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="owner in sortedOwners" :key="owner.id">
          <td class="owners-table__name">{{ owner.name }}</td>
          <td>{{ amountFor(owner.created) }}</td>
          <td>{{ amountFor(owner.open) }}</td>
          <td>{{ amountFor(owner.won) }}</td>
          <td>{{ amountFor(owner.lost) }}</td>
          <td>{{ Math.round(owner.conversion_rate) }}%</td>
        </tr>
      </tbody>
    </table>
    <empty-state v-else :title="$t('OPPORTUNITY_DASHBOARD.OWNERS.EMPTY')" />
  </div>
</template>

<script>
import EmptyState from 'dashboard/components/widgets/EmptyState';
import { formatCurrencyValue } from 'dashboard/helper/opportunityDashboardHelper';

export default {
  components: { EmptyState },
  props: {
    owners: { type: Array, default: () => [] },
    currency: { type: String, default: 'BRL' },
  },
  data() {
    return { mode: 'count' };
  },
  computed: {
    sortedOwners() {
      return [...this.owners].sort((a, b) => {
        const aHasActivity = a.created.count > 0;
        const bHasActivity = b.created.count > 0;

        if (aHasActivity !== bHasActivity) {
          return aHasActivity ? -1 : 1;
        }

        return a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });
    },
  },
  methods: {
    amountFor(bucket) {
      return this.mode === 'count'
        ? bucket.count
        : formatCurrencyValue(bucket.value, this.currency);
    },
  },
};
</script>

<style scoped lang="scss">
.panel-card {
  background: var(--white);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-large);
  overflow-x: auto;
  padding: var(--space-normal);
}

.panel-card__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-normal);
}

.panel-card__title {
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.toggle-group {
  background: var(--s-25);
  border-radius: var(--border-radius-medium);
  display: flex;
  padding: var(--space-micro);
}

.toggle-group__option {
  background: transparent;
  border: none;
  border-radius: var(--border-radius-small);
  color: var(--s-600);
  cursor: pointer;
  font-size: var(--font-size-mini);
  padding: var(--space-micro) var(--space-small);

  &--active {
    background: var(--white);
    color: var(--b-600);
    font-weight: var(--font-weight-bold);
  }
}

.owners-table {
  border-collapse: collapse;
  min-width: 100%;
  white-space: nowrap;

  th,
  td {
    padding: var(--space-small);
    text-align: left;
  }

  th {
    color: var(--s-500);
    font-size: var(--font-size-mini);
    font-weight: var(--font-weight-normal);
  }

  td {
    border-top: 1px solid var(--s-50);
    font-size: var(--font-size-small);
  }
}

.owners-table__name {
  font-weight: var(--font-weight-medium);
}
</style>
